<template>
  <FormSubmitPopup
    :title="title"
    v-model="value"
    :rightMethod="onSubmitted"
    @popupClose="$emit('input', false)"
    :rightButtonText="$t('submit')"
    ref="formPopup"
    hasAlert
    large>
    <template v-slot:input>
      <v-container class="compact" fluid>
        <v-layout row class="compact-layout">
          <v-flex md6>
            <v-card width="100%">
              <v-card-text>
                <v-layout align-baseline>
                  <v-flex class="title" md3>{{ $t('sender') }}: </v-flex>
                  <v-flex md4>
                    <vite-autocomplete
                      hide-details
                      :items="addresses"
                      name="address-template-name"
                      return-object
                      :label="$t('addressName')"
                      v-model="fromTemplate"
                      item-text="siteName"
                      item-value="siteName"></vite-autocomplete>
                  </v-flex>
                  <v-flex md2>
                    <v-checkbox hide-details class="checkbox" :label="$t('save')" v-model="isSaveFrom"></v-checkbox>
                  </v-flex>
                  <v-flex md4>
                    <v-text-field
                      hide-details
                      :label="$t('addressName')"
                      v-model.trim="from.siteName"
                      v-if="isSaveFrom"
                      :rules="[ fieldIsRequired('Site name'),  v => v === to.siteName && isSaveTo ? $t('siteNameIsSameAsToSite') : true]"
                      class="required_field"></v-text-field> 
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('fullName')"
                          v-model.trim="from.fullName"
                          :rules="[fieldIsRequired('Full name'),fieldIsFullName($t('fullName'))]"
                          class="required_field"
                        ></v-text-field> 
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('phone')"
                          class="required_field"
                          :rules="[fieldIsRequired('From phone'), fieldCharLimit('Phone', 10)]"
                          v-model.trim="from.phone"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('companyName')"
                      v-model.trim="from.company"
                      :rules="[fieldIsRequired()]"
                      class="required_field"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 1`"
                      v-model.trim="from.address1"
                      :rules="[fieldIsRequired('Address'), fieldCharLimit('Address1', 50)]"
                      class="required_field"></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 2`"
                      :rules="[fieldCharLimit('Address2', 50)]"
                      v-model.trim="from.address2"
                      ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('city')"
                          v-model.trim="from.city"
                          :rules="[fieldIsRequired('City')]"
                          class="required_field"
                          ></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <vite-autocomplete
                          hide-details
                          :items="states"
                          v-model="from.state"
                          :label="$t('state')"
                          :rules="[fieldIsRequired('State')]"
                          class="required_field"
                        >
                          <template v-slot:item="data">
                            <template>
                              <v-list-tile-content>
                                <v-list-tile-title :id="data.item">
                                  {{data.item}}
                                </v-list-tile-title>
                              </v-list-tile-content>
                            </template>
                          </template>
                        </vite-autocomplete>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('zipCode')"
                      v-model.trim="from.zipCode"
                      :rules="[fieldIsRequired('Zip code')]"
                      class="required_field"></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          label="EIN"
                          v-model.trim="from.partyId"
                          :rules="[fieldIsRequired(), fieldCharLimit('EIN', 9)]"
                          class="required_field"></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('email')"
                          v-model.trim="from.email"></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <vite-autocomplete
                      hide-details
                      :items="[{
                        name: $t('unitedStates'),
                        value: 'US'
                      }]"
                      item-text="name"
                      item-value="value"
                      v-model="from.countryCode"
                      :label="$t('country')"></vite-autocomplete>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex md6>
            <v-card width="100%">
              <v-card-text>
                <v-layout align-baseline>
                  <v-flex class="title" md3>{{ $t('recipient') }}: </v-flex>
                  <v-flex md4>
                    <vite-autocomplete
                      hide-details 
                      :items="addresses"
                      return-object
                      :label="$t('addressName')"
                      v-model="toTemplate"
                      item-text="siteName"
                      item-value="siteName"></vite-autocomplete>
                  </v-flex>
                  <v-flex md2>
                    <v-checkbox hide-details class="checkbox" :label="$t('save')" v-model="isSaveTo"></v-checkbox>
                  </v-flex>
                  <v-flex md4>
                    <v-text-field
                      hide-details
                      :label="$t('addressName')"
                      v-model.trim="to.siteName"
                      v-if="isSaveTo"
                      :rules="[ fieldIsRequired('Site name'),  v => v === from.siteName && isSaveFrom? this.$t('siteNameIsSameAsToSite') : true]"
                      class="required_field"></v-text-field> 
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('fullName')"
                          v-model.trim="to.fullName"
                          :rules="[ fieldIsRequired('Full name'), fieldCharLimit('Full name', 35) ]"
                          class="required_field"
                        />
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('phone')"
                          v-model.trim="to.phone"
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('companyName')"
                      v-model.trim="to.company"
                      :rules="[fieldIsRequired()]"
                    />
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 1`"
                      v-model.trim="to.address1"
                      :rules="[fieldIsRequired('Address'), fieldCharLimit('address', 50)]"
                      class="required_field"></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 2`"
                      :rules="[fieldCharLimit('Address2', 50)]"
                      v-model.trim="to.address2"
                      ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('city')"
                          v-model.trim="to.city"
                          :rules="[fieldIsRequired('City')]"
                          class="required_field"></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('state')"
                          v-model.trim="to.state"></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-layout justify-space-around align-baseline>
                      <v-flex>
                        <v-text-field
                          hide-details
                          :label="$t('zipCode')"
                          v-model.trim="to.zipCode"></v-text-field>    
                      </v-flex>
                      <v-flex xs2>
                        <v-tooltip top>
                          <template v-slot:activator="{on}">
                            <v-btn :disabled="googleMapDisabled" icon flat v-on="on" color="primary" @click.stop="goToGoogleMapLink" small><v-icon>location_on</v-icon></v-btn>
                          </template>
                          <span> {{ $t('googleMap') }}</span>
                        </v-tooltip>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          label="EIN"
                          v-model.trim="to.partyId"></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('email')"
                          v-model.trim="to.email"></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <vite-autocomplete
                      hide-details
                      :items="countryCodeArray"
                      item-text="name"
                      item-value="value"
                      v-model="to.countryCode"
                      :label="$t('country')"
                      :rules="[fieldIsRequired('Country')]"
                      class="required_field"></vite-autocomplete>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout align-baseline>
          <v-flex>
            <v-card width="100%" class="compact-card">
              <v-card-text>
                <v-layout align-baseline>
                  <v-flex class="title" md3>{{ $t('shipmentInfo') }}: </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs2>
                    <vite-autocomplete
                      hide-details
                      :items="[{
                        name: $t('directConsumer'),
                        value: 'directConsumer'
                      }, {
                        name: $t('governmentEntity'),
                        value: 'governmentEntity'
                      }, {
                        name: $t('reseller'),
                        value: 'reseller'
                      }, {
                        name: $t('other'),
                        value: 'other'
                      }]"
                      item-text="name"
                      item-value="value"
                      v-model="ultimateConsigneeType"
                      :label="$t('ultimateConsigneeType')"
                      readonly></vite-autocomplete>
                  </v-flex>
                  <v-flex xs2>
                    <v-autocomplete
                      hide-details
                      :label="$t('carrierName')"
                      :value="carrier.name"
                      :items="carrierItems"
                      item-text="name"
                      return-object
                      @change="item => carrier = item"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs2>
                    <v-autocomplete
                      hide-details
                      :label="$t('vesselName')"
                      :value="carrier.vesselName"
                      :items="carrierItems"
                      item-text="vesselName"
                      return-object
                      @change="item => carrier = item"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs2>
                    <v-autocomplete
                      hide-details
                      :label="$t('carrierCode')"
                      :value="carrier.code"
                      :items="carrierItems"
                      item-text="code"
                      return-object
                      @change="item => carrier = item"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex xs2>
                    <v-menu
                      :close-on-content-click="false"
                      v-model="datePicker"
                      :nudge-right="40"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <template v-slot:activator="data">
                        <v-text-field
                          v-model="onboardDate"
                          :label="$t('OnBoardDate')"
                          prepend-icon="event"
                          readonly
                          v-on="data.on"
                          :rules="[
                            fieldIsRequired('On board date')
                          ]"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        :min="endDateMinimum.toISOString()"
                        v-model="onboardDate" 
                        @input="datePicker = false"></v-date-picker>
                    </v-menu>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row class="compact-layout">
          <v-flex>
            <v-card width="100%">
              <v-card-text class="compact-card-text">
                <v-layout class="title compact-layout" align-baseline justify-start>
                    {{ $t('eei') }}:
                  <v-flex xs3 ml-4>
                    <v-layout>
                      <v-flex xs6>
                        <v-combobox
                          hide-details
                          :label="$t('commodityTemplate')"
                          :items="commodityTemplates"
                          item-text="name"
                          item-value="key"
                          clearable
                          @change="onSelectCommodityTemplate"
                        ></v-combobox>
                      </v-flex>
                      <v-flex xs4>
                        <v-text-field
                          ref="newTemplateNameInput"
                          hide-details
                          :label="$t('newTemplateName')"
                          v-model.trim="newTemplateName"
                          @keyup.enter="saveCommodityTemplate"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs2>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click="saveCommodityTemplate" :loading="isSavingTemplate" :disabled="isSavingTemplate">
                              <v-icon>save</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('saveAsTemplate') }}</span>
                        </v-tooltip>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex class="red--text font-weight-bold text-xs-right" v-if="commodityErrorMessage">
                    {{ commodityErrorMessage }}
                  </v-flex>
                </v-layout>
                <v-data-table
                  :headers="eeiHeaders"
                  :items="commodities"
                  hide-actions
                  class="elevation-2 mt-2"
                >
                  <template v-slot:headerCell="props">
                    <span v-if="props.header.value === 'action'">
                      <v-tooltip top>
                        <template v-slot:activator="tooltip">
                          <div v-on="tooltip.on">
                            <v-btn color="primary" flat small icon @click="addEmptyCommodity"><v-icon>add</v-icon></v-btn>
                          </div>
                        </template>
                        {{ $t('add') }}
                      </v-tooltip>
                    </span>
                    <span v-else>{{ props.header.text }}</span>
                  </template>
                  <template v-slot:items="props">
                    <tr
                      class="commodity-row"
                      :class="{'editing': editingRowIndex === props.index}"
                      @click="editRow(props.index, $event)"
                      @mouseleave="onRowMouseLeave"
                    >
                    <td class="text-xs-center">
                      <v-tooltip top>
                        <template v-slot:activator="{on}">
                          <v-btn color="error" flat small icon v-on="on" @click="deleteCommodity(props.index)" :disabled="commodities.length === 1"><v-icon>delete</v-icon></v-btn>
                        </template>
                        <span>{{ $t('delete') }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-xs-left">
                      <v-layout row>
                        <v-autocomplete
                          class="table-item"
                          style="max-width: 200px;"
                          :value="props.item.selectedCommodity"
                          :items="mergedCommoditiesList"
                          item-text="scheduleBDescription"
                          item-value="scheduleBDescription"
                          :filter="customCommodityFilter"
                          clearable
                          :label="$t('commodity')"
                          :placeholder="$t('commodities')"
                          @change="onComboboxChangeByCommodities($event, props.index)"
                          @input="onComboboxChangeByCommodities($event, props.index)"
                          :rules="[fieldIsRequired($t('commodity'))]"
                          :disabled="editingRowIndex !== props.index"
                          return-object
                        >
                          <template v-slot:item="data">
                            <v-list-tile-content style="max-width: 300px; white-space: normal;">
                              <v-list-tile-title style="white-space: normal; word-wrap: break-word;">{{ data.item.scheduleBDescription || data.item.description }}</v-list-tile-title>
                              <v-list-tile-sub-title style="white-space: normal; word-wrap: break-word;">
                                Code: {{ data.item.scheduleBCode }}
                              </v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                          <template v-slot:no-data>
                            <v-list-tile>
                              <v-list-tile-content>
                                <v-list-tile-title>
                                  {{ $t('noDataAvailable') }}
                                </v-list-tile-title>
                              </v-list-tile-content>
                            </v-list-tile>
                          </template>
                        </v-autocomplete>
                        <v-text-field
                          class="table-item"
                          style="max-width: 250px;"
                          v-model.trim="props.item.scheduleBDescription"
                          :label="$t('description')"
                          :placeholder="$t('description')"
                          :rules="[fieldIsRequired($t('description')), fieldCharLimit('description', 35)]"
                          :disabled="editingRowIndex !== props.index"
                        ></v-text-field>
                      </v-layout>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item required_field"
                        v-model.trim="props.item.scheduleBCode"
                        :rules="[fieldIsRequired('Schedule B Code'), fieldCharLimit('Schedule B Code', 10)]"
                        :disabled="true"
                        readonly
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-autocomplete
                          class="table-item required_field"
                          v-model="props.item.countryOfOrigin"
                          :items="countryOfOriginList"
                          item-text="name"
                          item-value="value"
                          :rules="[fieldIsRequired($t('countryOfOrigin'))]"
                          :disabled="editingRowIndex !== props.index"
                        ></v-autocomplete>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        :rules="[
                          fieldIsRequired(),
                          fieldIsInteger(),
                          fieldIsOverZero()
                        ]"
                        class="required_field pt-0 table-item qty_unit_filed"
                        v-model.number="props.item.quantity"
                        @input="onQuantityInput(props.item)"
                        :label="$t('quantity')"
                        :disabled="editingRowIndex !== props.index"
                        @click.native.stop
                      >
                        <template
                          slot="append-outer"
                        >
                          <v-autocomplete
                            class="qty_unit"
                            :items="inShowChannelUnionList"
                            v-model="props.item.quantityUnits"
                            clearable
                            :rules="[fieldIsRequired($t('unit'))]"
                            item-text="text"
                            item-value="key"
                            :label="$t('unit')"
                            :disabled="editingRowIndex !== props.index"
                          ></v-autocomplete>
                        </template>
                      </v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        v-if="isShowQty2(props.item)"
                        :rules="[
                          fieldIsRequired(),
                          fieldIsInteger(),
                          fieldIsOverZero()
                        ]"
                        class="required_field pt-0 table-item qty_unit_filed"
                        v-model.number="props.item.quantity2"
                        :label="$t('quantity')"
                        :disabled="editingRowIndex !== props.index"
                        @click.native.stop
                      >
                        <template
                          slot="append-outer"
                        >
                          <v-autocomplete
                            class="qty_unit"
                            :items="inShowUnit2List"
                            v-model="props.item.quantity2Units"
                            clearable
                            :rules="[fieldIsRequired($t('unit'))]"
                            item-text="text"
                            item-value="key"
                            :label="$t('unit')"
                            :disabled="editingRowIndex !== props.index"
                          ></v-autocomplete>
                        </template>
                      </v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item required_field"
                        hide-details
                        v-model.number="props.item.price"
                        @input="onPriceInput(props.item)"
                        prefix="$"
                        :rules="[fieldIsRequired($t('price')), fieldIsOverZero($t('price')), fieldIsInteger($t('price'))]"
                        :disabled="editingRowIndex !== props.index"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item required_field"
                        hide-details
                        :value="props.item.customsValue"
                        :rules="[fieldIsRequired($t('customsValue')), fieldIsOverZero($t('customsValue'))]"
                        :disabled="true"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item required_field"
                        hide-details
                        v-model.number="props.item.commodityWeight"
                        :suffix="isMeasurementMetric ? 'kg' : 'Lbs'"
                        :rules="[fieldIsRequired($t('totalWeight')), fieldIsOverZero($t('totalWeight'))]"
                        :disabled="editingRowIndex !== props.index"
                      ></v-text-field>
                    </td>
                    </tr>
                  </template>
                  
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>

      </v-container>
    </template>
  </FormSubmitPopup>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import EeiPackageWidget from './EeiPackageWidget'
import CommodityWidget from './CommodityWidget'
import { timeTools, checkRules, cloneDeep, toMoney, gotoGoogleMap, getRandomIdByTime, measurementTools, calculateQtyMethod, commodityTools } from '@/utils/tools'
import { mapGetters } from 'vuex'

export default {
  name: 'EeiEditPopup',
  components: {
    FormSubmitPopup,
    EeiPackageWidget,
    CommodityWidget
  },
  mixins: [
    checkRules,
    timeTools,
    measurementTools,
    calculateQtyMethod,
    commodityTools
  ],
  data () {
    return {
      title: JSON.stringify(this.eeiInEdit) === '{}' ? this.$t('addEEI') : 
        (this.isRecreate ? this.$t('copyEEI') : this.$t('editEEI')),
      packaging: '',
      signature: false,
      menu: false,
      newSenderName: '',
      from: {
        countryCode: 'US'
      },
      fromTemplate: {},
      to: {},
      toTemplate: {},
      rate: null,
      surcharge: null,
      insuredValue: 0,
      isLoadingRates: false,
      isSaveFrom: false,
      isSaveTo: false,
      quantity: 1,
      memo: '',
      note: '',
      errorMessage: '',
      warningMessages: {},
      name: '',
      rates: [],
      moreRatesToggle: false,
      shippingService: '',
      requestId: getRandomIdByTime(3),
      commodities: [],
      ultimateConsigneeType: 'reseller',
      carrier: {
        code: 'FDX',
        name: 'Fedex express',
        vesselName: 'FEDEX',
        loadingPortCode: '2006'
      },
      datePicker: false,
      onboardDate: new Date().toISOString().slice(0, 10),
      endDateMinimum: new Date(),
      commodityErrorMessage: '',
      newCommodity: {
        exportInformationCode: 'OS',
        licenseExceptionSymbol: 'C33',
        scheduleBDescription: '',
        scheduleBCode: '',
        countryOfOrigin: 'D',
        quantity: 1,
        quantityUnits: '',
        quantity2: 0,
        price: 0,
        quantity2Units: '',
        customsValue: 0,
        commodityWeight: 0,
        selectedCommodity: null
      },
      editingRowIndex: -1,
      newTemplateName: '',
      isSavingTemplate: false,
      countryOfOriginList: [{
        name: this.$t('domesticOrigin'),
        value: 'D'
      }, {
        name: this.$t('foreignOrigin'),
        value: 'F'
      }]
    }
  },
  beforeMount () {
    if (!this.scheduleBMap['0101210000'] && Object.keys(this.$store.getters.scheduleBMap || {}).length === 0) {
      this.$store.dispatch('getAESHTS').catch(error => {
        this.$store.dispatch('showToast', {
          level: 'error',
          info: this.$t('networkError')
        })
        throw error
      })
    }
  },
  async created () {
    if (this.$store.getters.organizationCommodities.length === 0) {
      await this.$store.dispatch('getOrganizationCommodities')
    }
  },
  async mounted () {
    if (JSON.stringify(this.eeiInEdit) !== '{}') {
      const {carrier, from, to, commodities, requestId, onboardDate} = this.eeiInEdit
      Object.assign(this, this.isRecreate ? {carrier, from, to, commodities, onboardDate} : 
        {carrier, from, to, commodities, requestId, onboardDate})
    }
    if (this.commodities.length === 0) {
      this.addEmptyCommodity()
    }
  },
  computed: {
    ...mapGetters(['commonCommodities', 'organizationCommodities', 'commoditiesList', 'commodityTemplates']),
    scheduleBMap () {
      return this.$store.getters.scheduleBMap || {}
    },
    states () {
      return this.$store.getters.stateAbbrevs
    },
    mergedCommoditiesList () {
      if (this.commoditiesList && this.commoditiesList.length > 0) {
        const mappedList = this.commoditiesList.map(item => {
          const origin = item.origin
          let countryOfOrigin = 'US'
          if (origin === 'D') {
            countryOfOrigin = 'US'
          } else if (origin === 'F') {
            countryOfOrigin = item.countryCode || 'CN'
          }
          return {
            scheduleBDescription: item.itemDesc || item.commodityDetail || '',
            description: item.itemDesc || item.commodityDetail || '',
            scheduleBCode: item.scheduleBCode,
            harmonizedCode: item.scheduleBCode,
            origin: origin,
            countryOfOrigin: countryOfOrigin,
            unit: item.unit,
            unit2: item.unit2,
            unitOfMeasure: item.unit,
            category: item.category
          }
        })
        return mappedList
      }
      return []
    },
    addresses () {
      return this.$store.getters.tenantAddresses
    },
    packagingMap () {
      return this.$store.getters.packagingMap
    },
    labelServices () {
      return this.$store.getters.labelServices
    },
    systemBalance () {
      return this.$store.getters.systemBalance
    },
    isMeasurementMetric () {
      return !!this.$store.getters.isMeasurementMetric
    },
    googleMapDisabled () {
      return !(
        this.to && (
          this.to.address1 &&
          this.to.city &&
          this.to.state &&
          this.to.zipCode
        )
      )
    },
    countryCodeArray () {
      return this.$store.getters.countryCodeArray
    },
    carrierItems () {
      return [{
        code: 'UPS',
        name: 'United Parcel Services',
        vesselName: 'UPS',
        loadingPortCode: '2006'
      }, {
        code: 'FDX',
        name: 'Fedex express',
        vesselName: 'FEDEX',
        loadingPortCode: '2006'
      }]
    },
    upsUnites2List () {
      return Object.entries(this.upsUnits2Map).map(([abbr, value]) => {
        return {text: abbr, key: abbr}
      })
    },
    upsUnits2Map () {
      return {
        'BA': 'Barrel',
        'BE': 'Bundle',
        'BG': 'Bag',
        'BH': 'Bunch',
        'BOX': 'Box',
        'BT': 'Bolt',
        'BU': 'Butt',
        'CI': 'Canister',
        'CM': 'Centimeter',
        'CON': 'Container',
        'CR': 'Crate',
        'CS': 'Case',
        'CT': 'Carton',
        'CY': 'Cylinder',
        'DOZ': 'Dozen',
        'EA': 'Each',
        'EN': 'Envelope',
        'FT': 'Feet',
        'KG': 'Kilogram',
        'KGS': 'Kilograms',
        'LB': 'Pound',
        'LBS': 'Pounds',
        'L': 'Liter',
        'M': 'Meter',
        'NMB': 'Number',
        'PA': 'Packet',
        'PAL': 'Pallet',
        'PC': 'Piece',
        'PCS': 'Pieces',
        'PF': 'Proof Liters',
        'PKG': 'Package',
        'PR': 'Pair',
        'PRS': 'Pairs',
        'RL': 'Roll',
        'SET': 'Set',
        'SME': 'Square Meters',
        'SYD': 'Square Yards',
        'TU': 'Tube',
        'YD': 'Yard'
      }
    },
    isScheduleBReady () {
      return !!this.scheduleBMap['0101210000']
    },
    upsUnitesMap () {
      return {
        'BBL': 'Barrels',
        'CAR': 'Carat',
        'CKG': 'Content Kilogram',
        'CM2': 'Square Centimeters',
        'CTN': 'Content Ton',
        'CUR': 'Curie',
        'CYK': 'Clean Yield Kilogram',
        'DOZ': 'Dozen',
        'DPC': 'Dozen Pieces',
        'DPR': 'Dozen Pairs',
        'FBM': 'Fiber Meter',
        'GCN': 'Gross Containers',
        'GM': 'Gram',
        'GRS': 'Gross',
        'HUN': 'Hundred',
        'KG': 'Kilogram',
        'KM3': '1,000 Cubic Meters',
        'KTS': 'Kilogram Total Sugars',
        'L': 'Liter',
        'M': 'Meter',
        'M2': 'Square Meters',
        'M3': 'Cubic Meters',
        'MC': 'Millicurie',
        'NO': 'Number',
        'PCS': 'Pieces',
        'PFL': 'Proof Liter',
        'PK': 'Pack',
        'PRS': 'Pairs',
        'RBA': 'Running Bales',
        'SQ': 'Square',
        'T': 'Ton',
        'THS': '1,000',
        'X': 'No Quantity required'
      }
    },
    upsUnitesList () {
      return Object.entries(this.upsUnitesMap).map(([abbr, value]) => {
        return {text: `${abbr}`, key: abbr}
      })
    },
    fedexHsCodeMap () {
      return {
        'AR': 'CARAT',
        'CM': 'CENTIMETER',
        'CFT': 'CUBIC FOOT',
        'DOZ': 'DOZEN',
        'DPR': 'DOZEN PAIR',
        'EA': 'EACH',
        'LFT': 'FOOT',
        'G': 'GRAM',
        'GR': 'GROSS',
        'KG': 'KILOGRAM',
        'LTR': 'LITER',
        'LB': 'POUND',
        'LNM': 'LINEAR METER',
        'M': 'METER',
        'M2': 'SQUARE METER (M2)',
        'M3': 'CUBIC METER',
        'MG': 'MILLIGRAM',
        'ML': 'MILLILITER',
        'NO': 'NUMBER',
        'OZ': 'OUNCE',
        'PR': 'PAIR',
        'PCS': 'PIECE',
        'SFT': 'SQUARE FOOT',
        'SYD': 'SQUARE YA,',
        'YD': 'YARD'
      }
    },
    fedexHsCodeList () {
      return Object.entries(this.fedexHsCodeMap).map(([abbr, value]) => {
        return {text: abbr, key: abbr}
      })
    },
    inShowUnit2List () {
      return this.carrier.code.includes('FDX') ? this.fedexHsCodeList : this.upsUnites2List
    },
    inShowChannelUnionList () {
      return this.carrier.code.includes('FDX') ? this.fedexHsCodeList : this.upsUnitesList
    },
    eeiHeaders () {
      const totalCustomsValue = this.commodities.reduce((acc, item) => acc + (item.customsValue || 0), 0)
      const totalQty = this.commodities.reduce((acc, item) => acc + (item.quantity || 0), 0)
      const totalQty2 = this.commodities.reduce((acc, item) => acc + (item.quantity2 || 0), 0)
      const totalWeight = this.commodities.reduce((acc, item) => acc + (item.commodityWeight || item.weight || 0), 0)
      
      return [
        { text: this.$t('actions'), value: 'action', align: 'center', sortable: false, width: '80px' },
        { text: this.$t('scheduleBDescription'), value: 'scheduleBDescription', align: 'left', sortable: false, width: '260px' },
        { text: this.$t('scheduleBCode'), value: 'scheduleBCode', align: 'left', sortable: false, width: '120px' },
        { text: this.$t('countryOfOrigin'), value: 'countryOfOrigin', align: 'left', sortable: false, width: '100px' },
        { text: this.$t('qty') + ` (${totalQty})`, value: 'quantity', align: 'left', sortable: false, width: '160px' },
        { text: this.$t('qty2') + ` (${totalQty2})`, value: 'quantity2', align: 'left', sortable: false, width: '100px' },
        { text: `${this.$t('price')}`, value: 'price', align: 'left', sortable: false, width: '90px' },
        { text: this.$t('customsValue') + ` ($${totalCustomsValue.toLocaleString()})`, value: 'customsValue', align: 'left', sortable: false, width: '80px' },
        { text: this.$t('totalWeight') + ` (${this.isMeasurementMetric ? 'kg' : 'Lbs'}) (${totalWeight})`, value: 'commodityWeight', align: 'left', sortable: false, width: '80px' }
      ]
    },
    editingCommodityScheduleBCode () {
      if (this.editingRowIndex !== null && this.commodities[this.editingRowIndex]) {
        return this.commodities[this.editingRowIndex].scheduleBCode
      }
      return ''
    }
  },
  watch: {
    toTemplate (value) {
      this.to = cloneDeep(this.toTemplate)
    },
    fromTemplate (value) {
      this.from = cloneDeep(this.fromTemplate)
    },
    shippingService (value) {
      if (['USPS', 'UPS_GROUND'].includes(value)) {
        this.quantity = 1
        this.packages && this.packages.forEach(pkg => {
          pkg.insuredValue = 0
          pkg.qty = 1
        })
      }
    },
    editingCommodityScheduleBCode (value, oldValue) {
      if (value !== oldValue && this.editingRowIndex !== -1 && !!value) {
        const item = this.commodities[this.editingRowIndex]
        if (item.scheduleBCode) {
          const unit = this.getUnitByCarrier(item)
          if (unit) {
            item.quantityUnits = unit || ''
          } else {
            item.quantityUnits = ''
          }
          const unit2 = this.getUnit2ByCarrier(item)
          if (unit2) {
            item.quantity2Units = unit2 || ''
          } else {
            item.quantity2Units = ''
          }
        }
      }
    }
  },
  methods: {
    toCeilInt (value) {
      if (isNaN(value)) return 0
      return parseInt(Math.ceil(value))
    },
    onQuantityInput (item) {
      item.customsValue = this.computeCustomsValue(item)
    },
    onPriceInput (item) {
      item.customsValue = this.computeCustomsValue(item)
    },
    computeCustomsValue (item) {
      if (!item.price || !item.quantity) return 0
      if (isNaN(item.price) || isNaN(item.quantity)) return 0
      return this.toCeilInt(Number(item.price * item.quantity))
    },
    toMoney,
    createAddress (addresses) {
      return this.$store.dispatch('labelCreateAddress', {addresses})
    },
    getUnitByCarrier (item) {
      if (!item || !item?.scheduleBCode) return ''
      const uom = this.getUnitOfMeasure(item.scheduleBCode, this.carrier.code)
      if (uom) {
        const info = this.inShowChannelUnionList.find(item => item.key === uom)
        if (info) {
          return info.key
        }
      }
      return ''
    },
    getUnit2ByCarrier (item) {
      if (!item || !item?.scheduleBCode) return ''
      const uom1 = this.isShowQty2(item) ? this.scheduleBMap[item.scheduleBCode].uom1 : ''
      if (uom1) {
        const info = this.inShowUnit2List.find(item => item.key === uom1)
        if (info) {
          return info.key
        }
      }
      return ''
    },
    getUnitOfMeasure (scheduleBCode = '', carrier = '') {
      let unitOfMeasure = ''
      carrier = carrier.trim().toUpperCase()
      const matchedItem = this.scheduleBMap[scheduleBCode]
      if (!matchedItem) {
        return unitOfMeasure
      }
      unitOfMeasure = matchedItem.uom
      return unitOfMeasure
    },
    extractPayload () {
      const {
        from,
        to,
        commodities = [],
        carrier = {},
        onboardDate
      } = this

      let {name, ...fromRest} = from
      let {address1, address2, ...rest} = to
      
      const payload = {
        from: {
          ...fromRest,
          partyIdType: 'E'
        },
        to: {
          ...rest,
          partyIdType: '',
          address1,
          address2: address2 || ''
        },
        note: this.labelNote || '',
        commodities: commodities.map(item => {
          const {commodityWeight, weight, selectedCommodity, ...rest} = item
          const matchedItem = this.scheduleBMap[item.scheduleBCode]
          
          rest.unitOfMeasure = (item.selectedUnit || this.getUnitOfMeasure(item.scheduleBCode, carrier.code)) || item.quantityUnits || ''
          
          if (item.quantity2 && item.quantity2 > 0) {
            rest.unitOfMeasure2 = (matchedItem && matchedItem.uom1 !== 'NULL' ? matchedItem.uom1 : '')
          }
          
          return {commodityWeight: commodityWeight, ...rest}
        }),
        carrier,
        onboardDate
      }
      return payload
    },
    onSubmitted () {
      const valid = this.$refs['formPopup'].validate()
      if (!valid) {
        return Promise.reject(Error(this.$t('dataMissing')))
      }
      const payload = this.extractPayload()
      if (!this.order) {
        delete payload.orderKey
      }
      let {isSaveFrom, isSaveTo, to, from, requestId} = this
      let {isAmazon: tempFlag, ...toRest} = to
      let {isAmazon, ...fromRest} = from
      let addressesToCreate = []
      isSaveTo && addressesToCreate.push(toRest)
      isSaveFrom && addressesToCreate.push(fromRest)
      addressesToCreate.length && this.createAddress(addressesToCreate)
      this.errorMessage = ''
      if (!payload.commodities.length) throw Error('Please add commodity before submit.')
      return this.$store.dispatch('processEei', {
        ...payload,
        requestId,
        type: JSON.stringify(this.eeiInEdit) === '{}' || this.isRecreate ? 'create' : 'update'
      })
        .then((res) => {
          this.$emit('submitted', {
            ...payload,
            requestId,
            res
          })
        })
        .catch(error => {
          if (error.errCode === 'endpoint-request-timed-out') {
            this.errorMessage = 'Endpoint request timed out. Please retry.'
          } else {
            this.errorMessage = error.data ? error.data.message : error.message
          }
          throw error
        })
    },
    deleteAddress (address) {
      if (!confirm('Are you sure to delete this address?')) return Promise.resolve()
      return this.$store.dispatch('labelDeleteAddress', address)
        .then(() => {
          if (this.from.siteName === address.siteName) {
            this.from = {}
          }
          if (this.to.siteName === address.siteName) {
            this.to = {}
          }
        })
    },
    setWarningMessages (key, value) {
      this.warningMessages[key] = value
    },
    deleteWarningMessage (...keys) {
      keys.forEach(key => {
        delete this.warningMessages[key]
      })
    },
    clearWarningMessages () {
      this.warningMessages = {}
    },
    formatEstDeliveryDate (str) {
      return (str === 'N/A' || !str) ? 'N/A' : this.toDateYYYYMMDD((new Date(str)))
    },
    goToGoogleMapLink () {
      gotoGoogleMap(this.to)
    },
    addEmptyCommodity () {
      this.commodityErrorMessage = ''

      this.commodities.push({
        exportInformationCode: 'OS',
        licenseExceptionSymbol: 'C33',
        scheduleBDescription: '',
        scheduleBCode: '',
        countryOfOrigin: 'D',
        quantity: 1,
        quantityUnits: '',
        quantity2: 0,
        price: 0,
        quantity2Units: '',
        customsValue: 0,
        commodityWeight: 0,
        selectedCommodity: null
      })
    },
    
    deleteCommodity (index) {
      if (this.commodities.length > 1) {
        this.commodities.splice(index, 1)
      } else {
        this.commodityErrorMessage = this.$t('atLeastOneCommodity')
      }
    },
    getQtySuffix (item) {
      if (!this.scheduleBMap[item.scheduleBCode]) return ''
      const unit = this.getUnitOfMeasure(item.scheduleBCode, this.carrier.code)
      return unit
    },
    isShowQty2 (item) {
      return this.scheduleBMap[item.scheduleBCode] && this.scheduleBMap[item.scheduleBCode].uom1 !== 'NULL'
    },
    getCommodityUpdates (selectedItem) {
      if (selectedItem === null || selectedItem === '') {
        return {
          scheduleBCode: '',
          selectedCommodity: null,
          exportInformationCode: 'OS',
          licenseExceptionSymbol: 'C33',
          scheduleBDescription: ''
        }
      }

      if (typeof selectedItem !== 'object') {
        return {}
      }

      const item = selectedItem

      const {
        scheduleBDescription,
        description,
        scheduleBCode,
        unit2
      } = item

      const fullDescription = scheduleBDescription || description || ''
      const isDescriptionTooLong = fullDescription.length > 35

      const updates = {
        scheduleBDescription: isDescriptionTooLong ? '' : fullDescription,
        scheduleBCode: scheduleBCode || '',
        selectedCommodity: item
      }

      if (unit2) {
        updates.quantity2Units = unit2
      }

      return updates
    },
    onComboboxChangeByCommodities (selectedItem, index) {
      this.commodityErrorMessage = ''

      const updates = this.getCommodityUpdates(selectedItem)
      Object.assign(this.commodities[index], updates)
    },
    
    async onSelectCommonCommodity (data) {
      if (!data) return

      const selectedItem = typeof data === 'string'
        ? this.commonCommodities.find(item => item.description === data)
        : data

      if (selectedItem) {
        const newCommodity = {
          exportInformationCode: 'OS',
          licenseExceptionSymbol: 'C33',
          scheduleBDescription: selectedItem.description,
          scheduleBCode: selectedItem.code || '',
          countryOfOrigin: 'D',
          customsValue: 0,
          commodityWeight: 0,
          quantity: 1,
          quantityUnits: '',
          price: 0,
          quantity2: 0,
          quantity2Units: ''
        }

        const unit = this.getUnitByCarrier(newCommodity)
        if (unit) {
          newCommodity.quantityUnits = unit
        }
        const unit2 = this.getUnit2ByCarrier(newCommodity)
        if (unit2) {
          newCommodity.quantity2Units = unit2
        }
        this.commodities.push(newCommodity)
      }
    },

    onSelectCommodityTemplate (template) {
      if (!template) return

      const templateData = typeof template === 'string'
        ? this.commodityTemplates.find(item => item.name === template)
        : template

      if (templateData && templateData.commodities && templateData.commodities.length > 0) {
        this.commodities = templateData.commodities.map(item => {
          const matchedCommodity = this.mergedCommoditiesList.find(c => c.scheduleBCode === item.scheduleBCode)

          const newCommodity = {
            exportInformationCode: item.exportInformationCode || 'OS',
            licenseExceptionSymbol: item.licenseExceptionSymbol || 'C33',
            scheduleBDescription: item.scheduleBDescription || '',
            scheduleBCode: item.scheduleBCode || '',
            countryOfOrigin: item.countryOfOrigin || 'D',
            quantity: item.quantity || 1,
            price: item.price || 0,
            quantity2: item.quantity2 || 0,
            commodityWeight: item.commodityWeight || 0,
            customsValue: (item.quantity || 0) * (item.price || 0),
            selectedCommodity: matchedCommodity || null
          }

          // Calculate units based on current carrier
          const unit = this.getUnitByCarrier(newCommodity)
          if (unit) {
            newCommodity.quantityUnits = unit
          } else {
            newCommodity.quantityUnits = item.selectedUnit || ''
          }

          const unit2 = this.getUnit2ByCarrier(newCommodity)
          if (unit2) {
            newCommodity.quantity2Units = unit2
          } else {
            newCommodity.quantity2Units = item.quantity2Units || ''
          }

          // If matched commodity found, use its country of origin info
          if (matchedCommodity) {
            // The matched commodity has 'origin' field (D/F) and 'countryOfOrigin' (US/CN/etc)
            // We need to convert back to D/F format for EEI
            if (matchedCommodity.origin === 'D') {
              newCommodity.countryOfOrigin = 'D'
            } else if (matchedCommodity.origin === 'F') {
              newCommodity.countryOfOrigin = 'F'
            }
          }

          return newCommodity
        })
      }
    },

    saveCommodityTemplate () {
      const templateName = this.newTemplateName

      if (!templateName || !templateName.trim()) {
        this.$store.dispatch('showToast', {info: this.$t('pleaseEnterTemplateName'), level: 'error'})
        return
      }

      const trimmedName = templateName.trim()

      const isDuplicate = this.commodityTemplates.some(template => template.name === trimmedName)
      if (isDuplicate) {
        this.$store.dispatch('showToast', {info: this.$t('templateNameAlreadyExists'), level: 'error'})
        return
      }

      if (!this.commodities || this.commodities.length === 0) {
        this.$store.dispatch('showToast', {info: this.$t('pleaseAddCommoditiesFirst'), level: 'error'})
        return
      }

      this.isSavingTemplate = true

      const payload = {
        name: trimmedName,
        description: trimmedName,
        commodities: this.commodities.map(item => ({
          scheduleBCode: item.scheduleBCode,
          scheduleBDescription: item.scheduleBDescription,
          countryOfOrigin: item.countryOfOrigin,
          quantity: item.quantity,
          quantityUnits: item.quantityUnits,
          quantity2: item.quantity2,
          quantity2Units: item.quantity2Units,
          price: item.price,
          commodityWeight: item.commodityWeight,
          exportInformationCode: item.exportInformationCode,
          licenseExceptionSymbol: item.licenseExceptionSymbol
        }))
      }

      this.$store.dispatch('updateCommodityTemplate', payload).then(() => {
        this.$store.dispatch('showToast', {info: this.$t('templateSavedSuccessfully'), level: 'success'})
        this.newTemplateName = ''
      }).catch(error => {
        this.$store.dispatch('showToast', {info: error.message || this.$t('failedToSaveTemplate'), level: 'error'})
        throw error
      }).finally(() => {
        this.isSavingTemplate = false
      })
    },

    editRow (index, event) {
      if (this.editingRowIndex === index) return

      this.editingRowIndex = index

      if (event) event.stopPropagation()
    },
    
    onRowMouseLeave (event) {
      if (event && event.relatedTarget) {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return
        }
        const selectMenus = document.querySelectorAll('.v-select-list, .v-menu__content')
        for (let menu of selectMenus) {
          if (menu.contains(event.relatedTarget)) {
            return
          }
        }
      }
      setTimeout(() => {
        const activeElement = document.activeElement
        if (activeElement && (
          activeElement.closest('.qty_unit') || 
          activeElement.closest('.v-select-list') ||
          activeElement.closest('.v-menu__content')
        )) {
          return
        }
        this.editingRowIndex = -1
      }, 100)
    },
    
    checkCommodityProfile (item) {
      if (!item.scheduleBDescription?.trim()) {
        this.commodityErrorMessage = this.$t('scheduleBDescriptionIsMissing')
        throw Error(this.$t('scheduleBDescriptionIsMissing'))
      }
      if (item.scheduleBDescription?.trim().length > 100) {
        this.commodityErrorMessage = this.$t('scheduleBDescriptionIsTooLong')
        throw Error(this.$t('scheduleBDescriptionIsTooLong'))
      }
      if (!item.scheduleBCode) {
        this.commodityErrorMessage = this.$t('scheduleBCodeIsMissing')
        throw Error(this.$t('scheduleBCodeIsMissing'))
      }
      if (item.scheduleBCode.length !== 10 || isNaN(+item.scheduleBCode)) {
        this.commodityErrorMessage = this.$t('scheduleBCodeIsInvalid')
        throw Error(this.$t('scheduleBCodeIsInvalid'))
      }

      return true
    },
    async saveCommodityProfile (item) {
      if (!this.checkCommodityProfile(item)) return Promise.reject(Error(this.commodityErrorMessage))
      if (item.isLoading) return
      this.$set(item, 'isLoading', true)
      this.commodityToSave = {...item}
      this.commodityErrorMessage = ''
      
      const {
        quantity, 
        isLoading, 
        selectedCommodity, 
        quantity2, 
        commodityWeight, 
        customsValue,
        quantityUnits,
        quantity2Units,
        price,
        ...rest
      } = this.commodityToSave
      
      rest.profileName = item.scheduleBDescription.trim()
      rest.scheduleBDescription = item.scheduleBDescription.trim()
      
      try {
        let target = await this.$store.dispatch('getCommodityKey', {keyword: rest.profileName, scheduleBDescription: rest.scheduleBDescription})
        if (target) {
          await this.$store.dispatch('addCommodity', {...rest, isUpdate: true, key: target})
        } else {
          await this.$store.dispatch('addCommodity', rest)
        }
        
        this.$store.dispatch('showToast', {info: this.$t('commodityProfileSaved'), level: 'success'})
        
        this.profileNameDialog = false
        
        return Promise.resolve()
      } catch (error) {
        this.commodityErrorMessage = error.message || this.$t('saveFailed')
        return Promise.reject(error)
      } finally {
        this.$set(item, 'isLoading', false)
      } 
    }
  },
  props: {
    value: Boolean,
    eeiInEdit: Object,
    initTemplate: {
      type: Object,
      default: () => {
        return {}
      }
    },
    isRecreate: Boolean,
    labelNote: {
      type: String,
      default: () => {
        return ''
      }
    },
    order: Object
  }
}
</script>

<style scoped>
.checkbox {
  margin-bottom: 0px; 
  position: relative; 
  top: 12px;
}
.checkbox >>> .v-label {
  font-size: 10px !important;
  top: 0px !important;
}

.compact {
  padding: 4px !important;
  zoom: 0.8;
  margin-bottom: 12px !important;
}

.compact .v-container {
  padding: 4px !important;
}

.compact .v-card {
  margin-bottom: 4px !important;
}

.compact .v-card__text {
  padding: 8px 16px !important;
}

.compact .v-layout {
  margin-top: 2px !important;
  margin-bottom: 2px !important;
}

.compact .v-text-field {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

.compact .compact-card .v-input >>> .v-label {
  top: 10px !important;
}

.compact .v-select {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.compact .v-input {
  margin-top: 8px !important;
}
.compact .v-input >>> .v-label {
  font-size: 14px !important;
  top: 14px;
}

.compact .v-data-table {
  margin-top: 4px !important;
}

.table-item {
  margin: 4px !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

.qty_unit_filed /deep/ .v-input__append-outer {
  margin-top: -8px !important;
}
.qty_unit_filed .v-input {
  margin-top: 0px;
}
.qty_unit_filed .qty_unit {
  margin: 0;
  padding: 0;
}

.qty_unit_filed .qty_unit /deep/ label::after {
  content: "";
}

.commodity-row {
  cursor: pointer;
  transition: all 0.2s;
}

.commodity-row:hover {
  background-color: rgba(18, 108, 149, 0.5);
}

.commodity-row .editing {
  background-color: rgba(33, 150, 243, 0.8);
}
</style>



// WEBPACK FOOTER //
// src/components/EeiEditPopup.vue